export class ApiError extends Error {
  readonly status: number;
  readonly data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }

  /** True when the endpoint itself is missing/not deployed rather than the request being wrong. */
  get isEndpointMissing(): boolean {
    return this.status === 404 || this.status === 501;
  }

  /** True when the request never reached the server (offline, DNS, CORS). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Send cookies — required if the API issues a session cookie on login. */
  credentials?: RequestCredentials;
}

const DEFAULT_TIMEOUT_MS = 20_000;

function extractMessage(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) return payload;

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    for (const key of ['message', 'detail', 'error']) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
    // DRF-style `{ field: ["msg"] }`
    for (const value of Object.values(record)) {
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }

  return fallback;
}

/**
 * Thin `fetch` wrapper: JSON in/out, request timeouts, and errors normalised to
 * {@link ApiError} so UI code branches on status instead of parsing strings.
 */
export async function request<TResponse>(url: string, options: RequestOptions = {}): Promise<TResponse> {
  const { method = 'GET', body, headers = {}, signal, timeoutMs = DEFAULT_TIMEOUT_MS, credentials } = options;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true });

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    throw new ApiError(
      aborted ? 'The request timed out. Please check your connection and try again.' : 'Network error. Please try again.',
      0,
      error,
    );
  } finally {
    clearTimeout(timeout);
  }

  const text = await response.text();
  let payload: unknown = text;
  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      // Non-JSON responses (HTML error pages) stay as raw text.
    }
  }

  if (!response.ok) {
    throw new ApiError(extractMessage(payload, `Request failed (${response.status})`), response.status, payload);
  }

  return payload as TResponse;
}
